/* Contained image viewer; native browser accessibility zoom remains enabled outside the surface. */
if (!customElements.get('gf-v31-zoom')) customElements.define('gf-v31-zoom', class extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('product-info');
    this.dialog = this.querySelector('dialog');
    this.surface = this.querySelector('[data-zoom-surface]');
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-gf-zoom], modal-opener[data-modal^="#ProductModal"]');
      const image = trigger?.querySelector('img');
      if (!image) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.open(image, trigger.querySelector('button') || trigger);
    }, { ...options, capture: true });
    this.addEventListener('click', event => {
      const action = event.target.closest('[data-zoom-action]')?.dataset.zoomAction;
      if (action === 'close') this.dialog.close();
      if (action === 'in') this.zoom(this.scale + .5);
      if (action === 'out') this.zoom(this.scale - .5);
      if (action === 'reset') this.zoom(1);
    }, options);
    this.dialog.addEventListener('close', () => this.restore(), options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoom(this.scale + .5); }
      if (event.key === '-') { event.preventDefault(); this.zoom(this.scale - .5); }
      if (event.key === '0') { event.preventDefault(); this.zoom(1); }
      if (this.scale > 1 && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) {
        event.preventDefault(); this.x += event.key === 'ArrowLeft' ? 40 : event.key === 'ArrowRight' ? -40 : 0;
        this.y += event.key === 'ArrowUp' ? 40 : event.key === 'ArrowDown' ? -40 : 0; this.paint();
      }
      if (event.key === 'Tab') {
        const buttons = [...this.querySelectorAll('button:not(:disabled)')];
        const first = buttons[0], last = buttons[buttons.length-1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus({preventScroll:true}); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus({preventScroll:true}); }
      }
    }, options);
    this.points = new Map();
    this.surface.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      this.points.set(event.pointerId, {x:event.clientX,y:event.clientY});
      this.surface.setPointerCapture(event.pointerId);
      this.lastDown = {x:event.clientX,y:event.clientY,time:performance.now(),moved:false};
      if (this.points.size > 1) { this.lastTap = null; this.lastDown.moved = true; }
      this.startGesture();
    }, options);
    this.surface.addEventListener('pointermove', event => {
      if (!this.points.has(event.pointerId)) return;
      event.preventDefault();
      if (Math.hypot(event.clientX-this.lastDown.x,event.clientY-this.lastDown.y)>8) this.lastDown.moved=true;
      this.points.set(event.pointerId,{x:event.clientX,y:event.clientY});
      const points=[...this.points.values()];
      if (points.length >= 2 && this.gesture?.distance) {
        const distance=Math.hypot(points[1].x-points[0].x,points[1].y-points[0].y);
        const mid={x:(points[0].x+points[1].x)/2,y:(points[0].y+points[1].y)/2};
        const scale=Math.max(1,Math.min(3,this.gesture.scale*distance/this.gesture.distance));
        const rect=this.surface.getBoundingClientRect();
        this.x=mid.x-rect.left-rect.width/2-this.gesture.anchorX*scale;
        this.y=mid.y-rect.top-rect.height/2-this.gesture.anchorY*scale;
        this.zoom(scale);
      } else if (points.length === 1 && this.scale > 1 && this.gesture) {
        this.x=this.gesture.x+points[0].x-this.gesture.pointerX;
        this.y=this.gesture.y+points[0].y-this.gesture.pointerY;this.paint();
      }
    }, options);
    for (const type of ['pointerup','pointercancel','lostpointercapture']) this.surface.addEventListener(type, event => {
      if (!this.points.has(event.pointerId)) return;
      if (type === 'pointerup' && event.pointerType !== 'mouse' && this.points.size === 1 && !this.lastDown?.moved && performance.now()-this.lastDown.time<300) {
        const tap={x:event.clientX,y:event.clientY,time:performance.now()};
        if (this.lastTap && tap.time-this.lastTap.time<320 && Math.hypot(tap.x-this.lastTap.x,tap.y-this.lastTap.y)<24) {
          this.zoom(this.scale>1 ? 1 : 2);this.lastTap=null;
        } else this.lastTap=tap;
      }
      if (type !== 'pointerup') this.lastTap=null;
      this.points.delete(event.pointerId);this.startGesture();
    }, options);
    window.addEventListener('resize', () => { if (this.dialog.open) { this.points.clear(); this.gesture=null; this.zoom(1); } }, options);
  }
  startGesture() {
    const points=[...this.points.values()];
    if (!points.length) { this.gesture=null;return; }
    this.gesture={x:this.x,y:this.y,scale:this.scale,pointerX:points[0].x,pointerY:points[0].y};
    if (points.length>=2) {
      const rect=this.surface.getBoundingClientRect();
      this.gesture.distance=Math.max(1,Math.hypot(points[1].x-points[0].x,points[1].y-points[0].y));
      this.gesture.anchorX=((points[0].x+points[1].x)/2-rect.left-rect.width/2-this.x)/this.scale;
      this.gesture.anchorY=((points[0].y+points[1].y)/2-rect.top-rect.height/2-this.y)/this.scale;
    }
  }
  open(source, trigger) {
    this.points.clear(); this.gesture=null; this.lastTap=null;
    this.trigger = trigger; this.image = source.cloneNode(true); this.image.removeAttribute('id'); this.image.loading = 'eager'; this.image.draggable = false;
    this.image.sizes = '95vw'; this.surface.replaceChildren(this.image);
    this.scroll = window.scrollY; this.oldBody = { position:document.body.style.position, top:document.body.style.top, width:document.body.style.width };
    document.body.style.position = 'fixed'; document.body.style.top = `-${this.scroll}px`; document.body.style.width = '100%';
    this.dialog.showModal(); this.zoom(1);
    this.image.addEventListener('load', () => this.paint(), {once:true,signal:this.abort.signal});
    this.querySelector('[data-zoom-action="close"]').focus({preventScroll:true});
  }
  zoom(scale) { this.scale = Math.max(1, Math.min(3, scale)); if (this.scale === 1) this.x = this.y = 0; this.paint(); }
  paint() {
    if (!this.image) return;
    const width = this.surface.clientWidth, height = this.surface.clientHeight;
    const fit = Math.min(width/(this.image.naturalWidth || width || 1), height/(this.image.naturalHeight || height || 1));
    const boundX = Math.max(0, ((this.image.naturalWidth || width)*fit*this.scale-width)/2);
    const boundY = Math.max(0, ((this.image.naturalHeight || height)*fit*this.scale-height)/2);
    this.x = Math.max(-boundX, Math.min(boundX, this.x || 0)); this.y = Math.max(-boundY, Math.min(boundY, this.y || 0));
    this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.querySelector('[data-zoom-percent]').textContent = `${Math.round(this.scale*100)}%`;
    this.querySelector('[data-zoom-action="out"]').disabled = this.scale === 1;
    this.querySelector('[data-zoom-action="in"]').disabled = this.scale === 3;
    this.surface.classList.toggle('is-zoomed',this.scale > 1);
  }
  restore() {
    if (!this.oldBody) return;
    Object.assign(document.body.style,this.oldBody); this.oldBody = null;
    window.scrollTo({top:this.scroll,behavior:'instant'}); this.trigger?.focus({preventScroll:true});
    this.points?.clear(); this.gesture=null; this.lastTap=null;
  }
  disconnectedCallback() { this.restore(); this.abort?.abort(); }
});
